import React, { useCallback, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { APIService } from 'api/APIService'
import { getSelectedCityId } from 'src/store/app.state.store'
import { Modal } from 'components/Utility/Modal/Modal'
import { StatusModal } from 'components/Utility/StatusModal/StatusModal'
import { ConfirmPhoneForm } from '../ConfirmPhone/ConfirmPhoneForm'
import { QuickSignUpForm, type QuickSignUpData } from './QuickSignUpForm'

type QuickSignUpControllerProps = {
  /** Gets the one-time token the booking is sent with */
  onSubmit: (signUpToken: string) => void,
  onCancel: () => void,
}

/** Booking without an account: name and phone, then the SMS code */
export const QuickSignUpController: React.FC<QuickSignUpControllerProps> = React.memo(({ onSubmit, onCancel }) => {
  const [stage, setStage] = useState<'form' | 'confirm'>('form')
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isSendingCode, setSendingCode] = useState(false)
  const [isConfirmingPhone, setConfirmingPhone] = useState(false)
  const [codeExpiresAt, setCodeExpiresAt] = useState<string | null>(null)
  const formData = useRef<QuickSignUpData | null>(null)
  const cityId = useSelector(getSelectedCityId)

  const sendConfirmCode = useCallback(async () => {
    if (!formData.current) {
      return
    }
    setSendingCode(true)
    try {
      const { expires_at: expiresAt } = await APIService.auth.confirmPhone(formData.current.phone, 'sign-up')
      setCodeExpiresAt(expiresAt)
    } catch (e) {
      setSubmitError((e as Error).message)
    } finally {
      setSendingCode(false)
    }
  }, [])

  const handleFormSubmit = useCallback(async (data: QuickSignUpData) => {
    // Browser autocomplete may fill an 8… number
    formData.current = { ...data, phone: data.phone.replace(/^8/, '7') }
    await sendConfirmCode()
    setStage('confirm')
  }, [sendConfirmCode])

  const handleConfirm = useCallback(async ({ code }: { code: string }) => {
    if (!formData.current) {
      return
    }
    const { phone, firstName, lastName } = formData.current
    setConfirmingPhone(true)
    try {
      onSubmit(await APIService.auth.quickSignUp({ firstName, lastName, phone, defaultCityId: cityId as number, code }))
    } catch (e) {
      setSubmitError((e as Error).message)
    } finally {
      setConfirmingPhone(false)
    }
  }, [cityId, onSubmit])

  return <>
    {stage === 'form' && (
      <Modal isOpen onClose={onCancel} back={onCancel}>
        <QuickSignUpForm data={formData.current} onSubmit={handleFormSubmit} isSubmitting={isSendingCode} />
      </Modal>
    )}
    {stage === 'confirm' && (
      <Modal isOpen back={() => setStage('form')} onClose={onCancel}>
        <ConfirmPhoneForm
          onResend={sendConfirmCode}
          onSubmit={handleConfirm}
          isSubmitting={isConfirmingPhone}
          reclaimAt={codeExpiresAt}
        />
      </Modal>
    )}
    <StatusModal
      isOpen={submitError !== null}
      modalText={submitError}
      status={'error'}
      buttonText={'ОК'}
      onClose={() => setSubmitError(null)}
      onButtonClick={() => setSubmitError(null)}
    />
  </>
})
QuickSignUpController.displayName = 'QuickSignUpController'
